import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useI18n } from '../context/I18nContext';
import { formatEuDate } from '../utils/dateFormat';
import './BillingManagement.css';

function BillingManagement() {
  const { apiFetch, hasPermission } = useAuth();
  const { language } = useI18n();
  
  const [loading, setLoading] = useState(false);
  const [records, setRecords] = useState([]);
  const [error, setError] = useState(null);
  
  // Search & Filter State
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [limit] = useState(50);
  const [offset, setOffset] = useState(0);
  const [total, setTotal] = useState(0);
  const [availableStatuses, setAvailableStatuses] = useState([]);

  const t = language === 'da'
    ? {
        billing: 'Fakturering',
        subtitle: 'Se alle okokbilling fakturaer',
        searchPlaceholder: 'Søg efter forfatter, modtager, selskab, vare eller ref ID...',
        refId: 'Ref',
        from: 'Fra',
        to: 'Til',
        society: 'Selskab',
        item: 'Vare',
        amount: 'Beløb',
        status: 'Status',
        fromDate: 'Fra dato',
        dueDate: 'For.',
        paidDate: 'Betalt',
        search: 'Søg',
        noData: 'Ingen fakturaer fundet',
        loading: 'Indlæser...',
        error: 'Fejl ved indlæsning',
        page: 'Side',
        of: 'af',
        showing: 'Viser',
        records: 'fakturaer',
        paid: 'Betalt',
        unpaid: 'Ubetalt',
        overdue: 'Forfaldet',
        cancelled: 'Annulleret',
        filterStatus: 'Status',
        all: 'Alle'
      }
    : {
        billing: 'Billing',
        subtitle: 'View all okokbilling invoices',
        searchPlaceholder: 'Search by author, receiver, society, item, or ref ID...',
        refId: 'Ref',
        from: 'From',
        to: 'To',
        society: 'Society',
        item: 'Item',
        amount: 'Amount',
        status: 'Status',
        fromDate: 'Sent',
        dueDate: 'Due',
        paidDate: 'Paid',
        search: 'Search',
        noData: 'No invoices found',
        loading: 'Loading...',
        error: 'Error loading data',
        page: 'Page',
        of: 'of',
        showing: 'Showing',
        records: 'records',
        paid: 'Paid',
        unpaid: 'Unpaid',
        overdue: 'Overdue',
        cancelled: 'Cancelled',
        filterStatus: 'Status',
        all: 'All'
      };

  const formatCurrency = (num) => {
    if (num === null || num === undefined) return '$0';
    const numValue = typeof num === 'string' ? parseFloat(num) : num;
    if (isNaN(numValue)) return '$0';
    return '$' + Math.round(numValue).toLocaleString();
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    try {
      return formatEuDate(dateStr);
    } catch (e) {
      return dateStr;
    }
  };

  const getStatusLabel = (status) => {
    const statusMap = {
      'paid': t.paid,
      'unpaid': t.unpaid,
      'overdue': t.overdue,
      'cancelled': t.cancelled
    };
    return statusMap[status] || status;
  };

  const copyToClipboard = (identifier, name) => {
    navigator.clipboard.writeText(identifier).then(() => {
      // Optional: You can add a toast notification here
      alert(`Copied: ${identifier}`);
    }).catch(err => {
      console.error('Failed to copy:', err);
    });
  };

  const fetchBilling = useCallback(async () => {
    if (!hasPermission('economy.view')) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      const params = new URLSearchParams();
      params.append('limit', limit);
      params.append('offset', offset);
      
      if (statusFilter && statusFilter !== 'all') {
        params.append('status', statusFilter);
      }
      
      if (searchQuery && searchQuery.trim()) {
        params.append('search', searchQuery.trim());
      }

      const response = await apiFetch(`/api/billing?${params.toString()}`);
      if (!response.ok) {
        throw new Error('Failed to fetch billing data');
      }

      const data = await response.json();
      setRecords(data.records || []);
      setTotal(data.pagination?.total || 0);
      setAvailableStatuses(data.filters?.statuses || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [hasPermission, apiFetch, limit, offset, statusFilter, searchQuery]);

  useEffect(() => {
    setOffset(0);
  }, [statusFilter, searchQuery]);

  useEffect(() => {
    fetchBilling();
  }, [fetchBilling]);

  if (!hasPermission('economy.view')) {
    return <div className="no-permission">{language === 'da' ? 'Adgang nægtet' : 'Access Denied'}</div>;
  }

  const totalPages = Math.ceil(total / limit);
  const currentPage = Math.floor(offset / limit) + 1;

  return (
    <div className="billing-container">
      {/* Header */}
      <div className="billing-header-section">
        <h1>{t.billing}</h1>
        <p className="subtitle">{t.subtitle}</p>
      </div>

      {/* Search and Filter Bar */}
      <div className="billing-search-bar">
        <div className="search-box">
          <input
            type="text"
            className="search-field"
            placeholder={t.searchPlaceholder}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          <span className="search-icon">⌕</span>
        </div>

        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="status-filter"
        >
          <option value="all">{t.filterStatus}: {t.all}</option>
          {availableStatuses && availableStatuses.map(s => (
            <option key={s} value={s}>{t.filterStatus}: {getStatusLabel(s)}</option>
          ))}
        </select>
      </div>

      {/* Error Message */}
      {error && (
        <div className="error-message">
          <strong>{t.error}:</strong> {error}
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div className="loading-state">
          <div className="spinner"></div>
          <p>{t.loading}</p>
        </div>
      )}

      {/* Table */}
      {!loading && records.length > 0 && (
        <>
          <div className="billing-table-wrapper">
            <table className="billing-table">
              <thead>
                <tr>
                  <th>{t.refId}</th>
                  <th>{t.from}</th>
                  <th>{t.to}</th>
                  <th>{t.society}</th>
                  <th>{t.item}</th>
                  <th>{t.amount}</th>
                  <th>{t.status}</th>
                  <th>{t.fromDate}</th>
                  <th>{t.dueDate}</th>
                </tr>
              </thead>
              <tbody>
                {records.map((record) => (
                  <tr key={record.id} className={`status-${record.status}`}>
                    <td className="cell-ref">{record.refId || '—'}</td>
                    <td 
                      className="cell-author clickable-cell" 
                      title={`Click to copy: ${record.authorIdentifier}`}
                      onClick={() => copyToClipboard(record.authorIdentifier, record.authorName)}
                    >
                      {record.authorName || '—'}
                    </td>
                    <td 
                      className="cell-receiver clickable-cell" 
                      title={`Click to copy: ${record.receiverIdentifier}`}
                      onClick={() => copyToClipboard(record.receiverIdentifier, record.receiverName)}
                    >
                      {record.receiverName || '—'}
                    </td>
                    <td className="cell-society" title={record.societyName}>{record.societyName || '—'}</td>
                    <td className="cell-item" title={record.item}>{record.item || '—'}</td>
                    <td className="cell-amount">{formatCurrency(record.totalAmount)}</td>
                    <td className="cell-status">
                      <span className={`status-badge status-${record.status}`}>
                        {getStatusLabel(record.status)}
                      </span>
                    </td>
                    <td className="cell-date">{formatDate(record.sentDate)}</td>
                    <td className="cell-date">{formatDate(record.limitPayDate)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="billing-pagination">
            <div className="pagination-info">
              {t.showing} {offset + 1}-{Math.min(offset + limit, total)} {t.of} {total} {t.records}
            </div>
            
            <div className="pagination-controls">
              <button
                className="pagination-btn"
                disabled={offset === 0}
                onClick={() => setOffset(Math.max(0, offset - limit))}
              >
                ← {language === 'da' ? 'Forrige' : 'Previous'}
              </button>

              <span className="page-indicator">
                {t.page} {currentPage} {t.of} {totalPages}
              </span>

              <button
                className="pagination-btn"
                disabled={currentPage >= totalPages}
                onClick={() => setOffset(offset + limit)}
              >
                {language === 'da' ? 'Næste' : 'Next'} →
              </button>
            </div>
          </div>
        </>
      )}

      {/* Empty State */}
      {!loading && records.length === 0 && !error && (
        <div className="empty-state">
          <div className="empty-icon">-</div>
          <p>{t.noData}</p>
          {searchQuery && (
            <p className="empty-tip">{language === 'da' ? 'Prøv at ændre dine søgekriteria' : 'Try changing your search criteria'}</p>
          )}
        </div>
      )}
    </div>
  );
}

export default BillingManagement;
